// Логиката на справката „Анулирани дейности": филтрите в колоните (Тип ·
// Анулирал · Склад), търсенето, сортирането и сумите. Периодът е по ДАТАТА НА
// АНУЛИРАНЕ, а бекендът връща последно анулираните отгоре — без избрано
// сортиране подредбата остава неговата. UI казва „анулиране", кодът остава
// `deleted`.
import type { DeletedMovement } from "../../../../api/inventory";
import type { FilterSpecs } from "../../components/filters/columnFilterModel";
import {
  compareText,
  matchesWords,
  searchWords,
  sortRows,
  type ReportSort,
} from "./reportListModel";

export const deletedByOf = (movement: DeletedMovement) => movement.deletedByName || "—";
export const warehousesOf = (movement: DeletedMovement) =>
  movement.warehouseNames.length ? movement.warehouseNames : ["—"];

export type DeletedFilters = { types: string[]; users: string[]; warehouses: string[] };
export type DeletedFilterKey = keyof DeletedFilters;
export const EMPTY_DELETED_FILTERS: DeletedFilters = { types: [], users: [], warehouses: [] };

// Документ в няколко склада (прехвърляне) се брои във всеки от тях
export const deletedFilterSpecs = ({
  titles,
  typeLabel,
}: {
  titles: Record<DeletedFilterKey, string>;
  typeLabel: (type: string) => string;
}): FilterSpecs<DeletedMovement, DeletedFilters> => ({
  types: { kind: "list", title: titles.types, valuesOf: (movement) => [movement.type], labelOf: typeLabel },
  users: { kind: "list", title: titles.users, valuesOf: (movement) => [deletedByOf(movement)] },
  warehouses: { kind: "list", title: titles.warehouses, valuesOf: warehousesOf },
});

export type DeletedSortKey =
  | "deletedAt"
  | "by"
  | "type"
  | "date"
  | "warehouse"
  | "products"
  | "net"
  | "gross";
export type DeletedSort = ReportSort<DeletedSortKey>;

export const deletedSortFirstDir = (key: DeletedSortKey) =>
  key === "by" || key === "type" || key === "warehouse" ? "asc" : "desc";

// Търсачката над таблицата — базата, върху която работят филтрите в колоните
export const searchDeleted = (
  movements: DeletedMovement[],
  query: string,
  typeLabel: (type: string) => string,
) => {
  const words = searchWords(query);
  if (!words.length) return movements;
  return movements.filter((movement) =>
    matchesWords(
      [
        typeLabel(movement.type),
        movement.deletedByName,
        movement.warehouseNames.join(" "),
        movement.invoiceNumber,
        movement.reason,
        movement.note,
        ...movement.lines.map((line) => line.productName),
      ],
      words,
    ),
  );
};

export const sortDeleted = (
  movements: DeletedMovement[],
  typeLabel: (type: string) => string,
  sort: DeletedSort | null,
) => {
  const compare = (a: DeletedMovement, b: DeletedMovement, key: DeletedSortKey) => {
    switch (key) {
      case "deletedAt":
        return a.deletedAt.localeCompare(b.deletedAt);
      case "by":
        return compareText(deletedByOf(a), deletedByOf(b));
      case "type":
        return compareText(typeLabel(a.type), typeLabel(b.type));
      case "date":
        return a.date.localeCompare(b.date);
      case "warehouse":
        return compareText(a.warehouseNames.join(", "), b.warehouseNames.join(", "));
      case "products":
        return (a.productsCount || 0) - (b.productsCount || 0);
      case "net":
        return (a.totalValueNoVat || 0) - (b.totalValueNoVat || 0);
      case "gross":
        return (a.totalValueWithVat || 0) - (b.totalValueWithVat || 0);
    }
  };
  return sortRows(movements, sort, compare);
};

export const deletedTotals = (movements: DeletedMovement[]) => ({
  count: movements.length,
  net: movements.reduce((sum, movement) => sum + (movement.totalValueNoVat || 0), 0),
  gross: movements.reduce((sum, movement) => sum + (movement.totalValueWithVat || 0), 0),
});
